import React, { useState, useEffect } from "react";
import {
  FaHeart,
  FaCartPlus,
  FaSearch,
  FaStar,
  FaRegStar,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Helmet } from "react-helmet-async";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

AOS.init();

function Shop() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [maxPrice, setMaxPrice] = useState(5000);
  const [sortOption, setSortOption] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [likedProducts, setLikedProducts] = useState([]);
  const [floatingHearts, setFloatingHearts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const itemsPerPage = 6;
  const { addToCart } = useCart();

  useEffect(() => {
    AOS.refresh();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();

        if (searchTerm) params.append("search", searchTerm);
        if (selectedCategories.length)
          selectedCategories.forEach((c) => params.append("category", c));
        if (maxPrice) params.append("maxPrice", maxPrice);
        if (sortOption) params.append("sort", sortOption);

        const res = await fetch(
          `${SERVER_URL}/api/products?${params.toString()}`,
        );
        const data = await res.json();
        // Ensure we store an array in state. API may return { products: [...] } or an error object.
        if (Array.isArray(data)) {
          setProducts(data);
        } else if (data && Array.isArray(data.products)) {
          setProducts(data.products);
        } else {
          console.warn("Unexpected products response:", data);
          setProducts([]);
        }
      } catch (err) {
        setError("We're having trouble loading products. Please try again.");
        console.error("Error fetching products:", err);
      }
      setLoading(false);
    };

    fetchProducts();
  }, [searchTerm, selectedCategories, maxPrice, sortOption]);

  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
    setCurrentPage(1);
  };

  // Defensive: ensure filteredProducts is always an array
  const filteredProducts = Array.isArray(products) ? products : [];

  const totalPages = Math.ceil((filteredProducts.length || 0) / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const toggleLike = async (productId) => {
    try {
      const response = await fetch(
        `${SERVER_URL}/api/products/like/${productId}`,
        {
          method: "POST",
        },
      );

      if (response.ok) {
        const data = await response.json();

        if (data.success) {
          const newHeart = { id: Date.now(), productId };

          // Update product likes in state
          setProducts((prev) =>
            prev.map((product) =>
              product._id === productId
                ? { ...product, likes: data.likes }
                : product,
            ),
          );

          // Trigger heart animation
          setFloatingHearts((prev) => [...prev, newHeart]);

          // Track liked products
          setLikedProducts((prev) =>
            prev.includes(productId) ? prev : [...prev, productId],
          );

          // Remove floating heart after animation
          setTimeout(() => {
            setFloatingHearts((prev) =>
              prev.filter((h) => h.id !== newHeart.id),
            );
          }, 1200);
        }
      }
    } catch (error) {
      console.error("❗ Failed to like product:", error);
    }
  };

  const categories = [
    "Men's Clothing",
    "Women's Clothing",
    "Kids' Clothing",
    "Shoes",
    "Bags & Accessories",
    "Clearance Sale",
  ];

  const tags = ["New Arrival", "Bestseller", "Featured"];

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? (
          <FaStar key={i} className="text-yellow-400" />
        ) : (
          <FaRegStar key={i} className="text-yellow-300" />
        ),
      );
    }
    return stars;
  };

  return (
    <>
      <Helmet>
        <title>Shop Stylish Clothing Online | Pak Fashions Kenya</title>
        <meta
          name="description"
          content="Browse and shop elegant men's, women's and kids' clothing, shoes, bags and accessories at unbeatable prices. Fast delivery in Kenya."
        />
        <meta
          name="keywords"
          content="shop clothes Kenya, affordable fashion, men's wear, women's dresses, Nanyuki, kids wear, bags, accessories"
        />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="Shop Online | Pak Fashions" />
        <meta
          property="og:description"
          content="Discover Pak Fashions collection of trendy, quality, and affordable clothing. Fast local delivery."
        />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/PakFashions-logo.jpg"
        />
        <meta property="og:url" content="https://pakfashions.co.ke/shop" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white min-h-screen px-2 py-8 sm:px-6 md:px-8 lg:px-20 mt-16 text-black">
        {/* Hero */}
        <header className="text-center mb-12 md:mb-16" data-aos="fade-down">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-gray-900">
            Shop Our Collection
          </h1>
          <p className="text-gray-500 font-medium text-lg max-w-2xl mx-auto">
            Discover stylish pieces made with love and tradition. Quality
            fashion for everyone.
          </p>
          <div className="h-1 w-24 bg-yellow-400 rounded-full mx-auto mt-6"></div>
        </header>

        <div className="w-full max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 lg:gap-12">
          {/* Filters Sidebar */}
          <aside className="w-full md:w-auto bg-gray-50 p-6 rounded-3xl border border-gray-100 md:sticky top-24 self-start text-black shadow-sm min-w-[220px] max-w-full md:max-w-xs">
            <div className="space-y-8">
              {/* Search */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 tracking-tight">
                  Search
                </h2>
                <div className="relative group">
                  <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-yellow-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="Find something..."
                    className="w-full pl-11 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition-all outline-none"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              {/* Categories */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 tracking-tight">
                  Categories
                </h3>
                <div className="flex flex-col space-y-3 max-h-52 overflow-auto pr-2 custom-scrollbar">
                  {categories.map((cat) => (
                    <label
                      key={cat}
                      className="inline-flex items-center space-x-3 text-gray-600 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat)}
                        onChange={() => handleCategoryChange(cat)}
                        className="form-checkbox h-5 w-5 text-yellow-500 rounded border-gray-300 focus:ring-yellow-400 transition-colors cursor-pointer"
                      />
                      <span className="text-base group-hover:text-gray-900 transition-colors">
                        {cat}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Max Price */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                    Price
                  </h3>
                  <span className="text-sm font-semibold bg-white px-2 py-1 rounded-md border border-gray-200">
                    Ksh {maxPrice}
                  </span>
                </div>
                <input
                  type="range"
                  min="100"
                  max="5000"
                  value={maxPrice}
                  onChange={(e) => {
                    setMaxPrice(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full accent-yellow-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-2 font-medium">
                  <span>Ksh 100</span>
                  <span>Ksh 5000</span>
                </div>
              </div>

              {/* Tags */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 tracking-tight">
                  Filters
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-white border border-gray-200 text-gray-600 px-4 py-1.5 rounded-lg text-sm font-medium hover:border-yellow-400 hover:text-yellow-600 cursor-pointer transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="w-full flex flex-col min-w-0">
            {/* Sort */}
            <div
              className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100"
              data-aos="fade-left"
            >
              <div className="text-sm font-semibold text-gray-500">
                Showing{" "}
                <span className="text-gray-900">{filteredProducts.length}</span>{" "}
                Results
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">
                  Sort by:
                </span>
                <select
                  className="w-full sm:w-auto bg-white border border-gray-200 py-2.5 px-4 rounded-xl text-gray-700 text-sm font-medium hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-100 transition-shadow transition-colors cursor-pointer"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                >
                  <option value="">Recommended</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="newest">Newest Arrivals</option>
                  <option value="rated">Best Rated</option>
                </select>
              </div>
            </div>

            {/* Product Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <div
                    key={n}
                    className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 flex flex-col justify-between"
                  >
                    <div className="w-full h-64 bg-gray-200 animate-pulse rounded-2xl mb-4"></div>
                    <div className="space-y-3 px-2 mb-2">
                      <div className="w-3/4 h-5 bg-gray-200 animate-pulse rounded"></div>
                      <div className="w-1/3 h-4 bg-gray-200 animate-pulse rounded"></div>
                    </div>
                    <div className="px-2 mt-4 flex gap-3">
                      <div className="w-12 h-10 bg-gray-200 animate-pulse rounded-full"></div>
                      <div className="flex-1 h-10 bg-gray-200 animate-pulse rounded-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-600 border border-red-200 p-6 rounded-2xl text-center flex flex-col items-center">
                <span className="text-4xl mb-3">😕</span>
                <p className="font-medium text-lg">{error}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 pb-10">
                {paginatedProducts.length === 0 && (
                  <div className="col-span-full py-16 text-center">
                    <p className="text-gray-500 text-lg mb-2">
                      No products found matching your criteria.
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCategories([]);
                        setMaxPrice(5000);
                      }}
                      className="text-yellow-600 font-semibold hover:underline"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
                {paginatedProducts.map((product) => (
                  <article
                    key={product._id}
                    className="bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                    data-aos="zoom-in"
                  >
                    <div className="relative p-3 pb-0">
                      {/* Floating hearts */}
                      {floatingHearts
                        .filter((h) => h.productId === product._id)
                        .map((heart) => (
                          <div
                            key={heart.id}
                            className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-red-500 text-4xl pointer-events-none z-20"
                            style={{
                              animation: "floatHeart 1s ease-out forwards",
                            }}
                          >
                            💖
                          </div>
                        ))}
                      <Link to={`/product/${product._id}`}>
                        <div className="h-64 overflow-hidden rounded-2xl bg-gray-50 relative">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                          />
                          {product.tag && (
                            <span className="absolute top-3 left-3 bg-yellow-400 text-black text-xs px-3 py-1 rounded-full font-bold tracking-wide shadow-sm">
                              {product.tag}
                            </span>
                          )}
                        </div>
                      </Link>
                    </div>
                    <div className="p-5 flex flex-col justify-between">
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg mb-1 truncate group-hover:text-yellow-600 transition-colors">
                          {product.name}
                        </h3>
                        <div className="flex items-center space-x-1.5 mb-3">
                          <div className="flex">
                            {renderStars(product.rating)}
                          </div>
                          <span className="text-xs text-gray-400 font-medium mt-0.5">
                            ({product.rating})
                          </span>
                        </div>
                        <p className="text-xl font-extrabold text-black">
                          Ksh {product.price.toLocaleString()}
                        </p>
                      </div>
                      <div className="mt-5 flex justify-between items-center gap-3">
                        <button
                          onClick={() => toggleLike(product._id)}
                          className={`p-3 rounded-xl transition-colors ${
                            likedProducts.includes(product._id)
                              ? "bg-red-50 text-red-500 hover:bg-red-100"
                              : "bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-red-400"
                          }`}
                          title="Like this product"
                        >
                          <FaHeart size={20} />
                        </button>
                        <button
                          onClick={() => addToCart(product)}
                          className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
                        >
                          <FaCartPlus size={18} /> Add to Cart
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {/* Pagination */}
            {totalPages > 1 && (
              <nav
                className="mt-8 mb-4 w-full flex justify-center items-center"
                aria-label="Pagination"
                data-aos="fade-up"
              >
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent px-2 max-w-full">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 disabled:opacity-50 hover:bg-gray-50 transition-colors shrink-0"
                  >
                    &lt;
                  </button>
                  <div className="flex gap-1">
                    {[...Array(totalPages).keys()].map((page) => (
                      <button
                        key={page + 1}
                        onClick={() => setCurrentPage(page + 1)}
                        className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-colors shrink-0 ${
                          currentPage === page + 1
                            ? "bg-yellow-400 text-black shadow-sm"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {page + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 disabled:opacity-50 hover:bg-gray-50 transition-colors shrink-0"
                  >
                    &gt;
                  </button>
                </div>
              </nav>
            )}
          </main>
        </div>
      </div>
    </>
  );
}

export default Shop;
